import moment from 'moment-timezone'
import {
  format,
  min,
} from 'date-fns'
import {
  Alert,
  AlertAPI,
  AlertGroup,
  AlertStatus,
  HistoricalAlert,
  HistoricalAlertGroupApi,
  HistoricalAlertsApi,
} from '@data/alerts/model/alert.model'

// Pure mapping of the /usage_alerts payloads into the frontend alert models.
// No React, API or store imports — the query hooks in alerts.ts call these, and keeping
// them dependency-free is what makes them directly unit-testable (mapAlerts.test.ts).
// Groups key on gid rather than on any device identifier: one device can hold several
// concurrent groups. See docs/alert-mapper-defects-plan.md.

export const mapAlertGroups = (alerts: AlertAPI[]): AlertGroup[] => {
  const alertsByGroup = alerts.reduce((acc: { [key: string]: AlertAPI[] }, alert: AlertAPI) => {
    const key: string = alert.gid
    if (!acc[key]) {
      acc[key] = [alert]
    } else {
      acc[key].push(alert)
    }
    return acc
  }, {})
  const alertGroups: AlertGroup[][] = Object.values(alertsByGroup).reduce(
    (acc: AlertGroup[][], groupAlerts: AlertAPI[]) => {
      // Group-level ackBy credits the most recent acknowledgement. Compared with
      // moment rather than string order: ackDate mixes fractional-second precision
      // ("...:04Z" vs "...:04.950000Z"), which sorts wrongly lexicographically.
      let latestAckDate: string | null = null
      const splitAlerts = groupAlerts
        .sort((a: AlertAPI, b: AlertAPI) => moment(a.alertTime).diff(b.alertTime))
        .reduce((splitArray: AlertGroup[], alert: AlertAPI) => {
          if (splitArray.length === 0) {
            splitArray.push({
              gid: alert.gid,
              deviceId: alert.deviceId,
              dlId: alert.dlId,
              dUuid: alert.dUuid,
              deviceLocation: alert.deviceLocation,
              priorityTier: alert.ackResult,
              startAlertTime: alert.alertTime,
              endAlertTime: alert.alertTime,
              startAlertTimeUTC: alert.utcTime,
              endAlertTimeUTC: alert.utcTime,
              status: mapStatus(alert.status),
              ackBy: alert.ackBy,
              alerts: [
                {
                  id: alert.id,
                  time: alert.alertTime,
                  utcTime: alert.utcTime,
                  priorityTier: alert.ackResult,
                  createdAt: alert.createdAt,
                  alertScore: alert.alertScore,
                  status: mapStatus(alert.status),
                  triggerType: alert.triggerType,
                  actioned: alert.actioned,
                  ackBy: alert.ackBy,
                  estimatedLeak: alert.estimatedLeak,
                  valid: alert.valid,
                  gid: alert.gid,
                  hourlyAlertsSent: alert.hourlyAlertsSent,
                },
              ],
              hourlyAlertsSentDate: alert.hourlyAlertsSentDate,
              estimatedLeakTotal: alert.estimatedLeak ?? 0,
              alertCloseDate: alert.closedDate,
            })
            latestAckDate = alert.ackDate
            return splitArray
          }

          const lastGroup = splitArray[splitArray.length - 1]
          splitArray[splitArray.length - 1].endAlertTime = alert.alertTime
          splitArray[splitArray.length - 1].endAlertTimeUTC = alert.utcTime
          splitArray[splitArray.length - 1].hourlyAlertsSentDate =
            alert.hourlyAlertsSentDate ??
            splitArray[splitArray.length - 1].hourlyAlertsSentDate
          splitArray[splitArray.length - 1].alerts.push({
            id: alert.id,
            time: alert.alertTime,
            utcTime: alert.utcTime,
            createdAt: alert.createdAt,
            priorityTier: alert.ackResult,
            alertScore: alert.alertScore,
            status: mapStatus(alert.status),
            triggerType: alert.triggerType,
            actioned: alert.actioned,
            ackBy: alert.ackBy ?? undefined,
            estimatedLeak: alert.estimatedLeak,
            valid: alert.valid,
            gid: alert.gid,
            hourlyAlertsSent: alert.hourlyAlertsSent,
          })
          splitArray[splitArray.length - 1].estimatedLeakTotal += alert.estimatedLeak ?? 0
          const alertAckBy = alert.ackBy ?? undefined
          if (
            alertAckBy !== undefined &&
            (latestAckDate == null ||
              (alert.ackDate != null && moment(alert.ackDate).isAfter(latestAckDate)))
          ) {
            latestAckDate = alert.ackDate
            splitArray[splitArray.length - 1].ackBy = alertAckBy
          }
          if (
            lastGroup.priorityTier == null ||
            (alert.ackResult != null && lastGroup.priorityTier > alert.ackResult)
          ) {
            splitArray[splitArray.length - 1].priorityTier = alert.ackResult
          }
          // New alerts join an ongoing group as 'open'. Once any alert has been
          // actioned the group reflects that: in_progress or closed outranks open.
          const alertStatus = mapStatus(alert.status)
          if (alertStatus > splitArray[splitArray.length - 1].status) {
            splitArray[splitArray.length - 1].status = alertStatus
          }
          return splitArray
        }, [] as AlertGroup[])
      acc.push(splitAlerts)
      return acc
    },
    [] as AlertGroup[][],
  )
  return alertGroups
    .reduce((acc: AlertGroup[], item: AlertGroup[]) => {
      acc.push(...item)
      return acc
    }, [])
    .sort((a: any, b: any) => -moment(a.endAlertTime).diff(b.endAlertTime))
}

export const mapHistoricalAlerts = (data: HistoricalAlertsApi[]): HistoricalAlert[] => {
  return data.map((a: HistoricalAlertsApi) => {
    return {
      deviceId: a.deviceId,
      dlId: a.dlId,
      dUuid: a.dUuid,
      tier1: a.alerts.find((h: HistoricalAlertGroupApi) => h.ackResult === 1)?.groups ?? [],
      tier2: a.alerts.find((h: HistoricalAlertGroupApi) => h.ackResult === 2)?.groups ?? [],
      tier3: a.alerts.find((h: HistoricalAlertGroupApi) => h.ackResult === 3)?.groups ?? [],
      tier1Total: a.alerts.find((h: HistoricalAlertGroupApi) => h.ackResult === 1)?.alertCount ?? 0,
      tier2Total: a.alerts.find((h: HistoricalAlertGroupApi) => h.ackResult === 2)?.alertCount ?? 0,
      tier3Total: a.alerts.find((h: HistoricalAlertGroupApi) => h.ackResult === 3)?.alertCount ?? 0,
      total: a.total,
    }
  })
}

// GET /usage_alerts/{dUuid}/history/{gid} — one closed group's alerts.
//
// The endpoint returns records sorted by utcTime DESCENDING, so index 0 is the newest and
// the last element the oldest. That ordering is an API contract, not an observation, which
// is why the ends are taken directly rather than re-sorted.
//
// `alerts` is returned oldest-first to match mapAlertGroups, so AlertGroup.alerts means the
// same thing regardless of which endpoint produced the group.
export const mapHistoricalAlertGroup = (
  alerts: AlertAPI[],
  device: { deviceId: string, deviceName: string, uom: string },
  selection: { priorityTier: number },
): AlertGroup | null => {
  if (alerts.length === 0) return null

  const newest = alerts[0]
  const oldest = alerts[alerts.length - 1]

  const sentDates = alerts
    .map((alert) => alert.hourlyAlertsSentDate)
    .filter((date): date is string => !!date)
    .map((date) => new Date(date))

  return {
    gid: newest.gid,
    deviceId: device.deviceId,
    deviceLocation: device.deviceName,
    deviceUom: device.uom,
    priorityTier: selection.priorityTier,
    status: AlertStatus.CLOSED,
    startAlertTime: oldest.alertTime,
    startAlertTimeUTC: oldest.utcTime,
    endAlertTime: newest.alertTime,
    endAlertTimeUTC: newest.utcTime,
    alertCloseDate: newest.closedDate,
    // Format preserved verbatim from the component this moved out of — nothing reads the
    // value beyond truthiness today, but a refactor is the wrong place to change it.
    hourlyAlertsSentDate: sentDates.length === 0
      ? null
      : format(min(sentDates), 'yyyy-MM-dd\'T\'HH:mm:ss.SSSSSSSxx'),
    dlId: newest.dlId,
    dUuid: newest.dUuid,
    estimatedLeakTotal: alerts.reduce((total, alert) => total + (alert.estimatedLeak ?? 0), 0),
    alerts: alerts
      .map((alert): Alert => ({
        id: alert.id,
        gid: alert.gid,
        time: alert.alertTime,
        utcTime: alert.utcTime,
        createdAt: alert.createdAt,
        priorityTier: alert.ackResult,
        alertScore: alert.alertScore,
        status: AlertStatus.CLOSED,
        triggerType: alert.triggerType,
        estimatedLeak: alert.estimatedLeak,
        hourlyAlertsSent: alert.hourlyAlertsSent,
        actioned: alert.actioned,
        valid: alert.valid,
      }))
      .reverse(),
  }
}

function mapStatus(status: string): AlertStatus {
  switch (status) {
    case 'open':
      return AlertStatus.OPEN
    case 'in_progress':
      return AlertStatus.IN_PROGRESS
    case 'closed':
      return AlertStatus.CLOSED
  }
  return AlertStatus.OPEN
}